import { Injectable, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { 
  LoginRequest, 
  LoginResponse, 
  LogoutResponse,
  RefreshResponse,
  ForgotPasswordRequest, 
  ForgotPasswordResponse, 
  ResetPasswordRequest, 
  ResetPasswordResponse, 
  User, 
  AuthState 
} from './auth.models';
@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private readonly API_BASE = `${environment.apiBaseUrl}/auth`;
  private refreshTimer: any; // Timer for automatic token refresh
  private readonly REFRESH_THRESHOLD_MS = 2 * 60 * 1000; // Refresh 2 minutes before expiry
  private tokenExpiresAt: number = 0; // Timestamp when token expires
  private visibilityChangeHandler: (() => void) | null = null;
  private isRefreshing = false; // Prevent concurrent refresh calls
  private readonly VIEW_AUCTIONEER_KEY = 'viewAuctioneerId'; // localStorage key for persisting auctioneer selection

  private authState = new BehaviorSubject<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: true,
    error: null,
    viewAuctioneerId: null
  });
  public authState$ = this.authState.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private snackBar: MatSnackBar,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Initialize auth state to loading
    this.updateAuthState({ ...this.authState.value, loading: true });

    // Set up visibility change listener to handle tab switching
    this.setupVisibilityListener();

    // Auth initialization will be handled by APP_INITIALIZER
    // This ensures proper timing and prevents race conditions
  }
  /**
   * Initialize authentication on app startup
   */
  initializeAuth(): Promise<void> {
    return new Promise((resolve) => {
      // Only initialize auth on browser platform to avoid SSR issues
      if (!isPlatformBrowser(this.platformId)) {
        this.updateAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: null
        });
        resolve();
        return;
      }
      this.updateAuthState({ ...this.authState.value, loading: true });
      // Set a timeout to prevent infinite loading state
      const initTimeout = setTimeout(() => {
        this.updateAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: null
        });
        resolve();
      }, 15000); // Increased to 15 second timeout to allow for slower networks
      // Add a small delay to ensure DOM and cookies are fully loaded
      setTimeout(() => {
        // Check if user is authenticated via cookies
        this.checkAuthStatus().subscribe({
          next: (response) => {
            clearTimeout(initTimeout);
            // Backend returns user data on success
            if (response.user) {
              // Restore viewAuctioneerId from localStorage for super_admin
              let viewAuctioneerId = null;
              if (response.user.role === 'super_admin' && isPlatformBrowser(this.platformId)) {
                const stored = localStorage.getItem(this.VIEW_AUCTIONEER_KEY);
                viewAuctioneerId = stored || null;
              }
              
              this.updateAuthState({
                isAuthenticated: true,
                user: response.user,
                loading: false,
                error: null,
                viewAuctioneerId: viewAuctioneerId
              });
              // Start the auto-refresh timer
              this.startAutoRefresh();
            } else {
              this.updateAuthState({
                isAuthenticated: false,
                user: null,
                loading: false,
                error: null
              });
            }
            resolve();
          },
          error: (error) => {
            clearTimeout(initTimeout);
            // 401 Unauthorized is expected when user is not logged in
            // Don't log it as an error, just set unauthenticated state
            if (error.status === 401) {
            } else {
            }
            this.updateAuthState({
              isAuthenticated: false,
              user: null,
              loading: false,
              error: null
            });
            resolve();
          }
        });
      }, 100); // 100ms delay to ensure DOM and cookies are ready
    });
  }
  /**
   * Retry authentication check with exponential backoff
   */
  private retryAuthCheck(attempt = 1, maxAttempts = 3): Promise<void> {
    return new Promise((resolve) => {
      this.checkAuthStatus().subscribe({
        next: (response) => {
          if (response.user) {
            this.updateAuthState({
              isAuthenticated: true,
              user: response.user,
              loading: false,
              error: null
            });
            this.startAutoRefresh();
          } else {
            this.updateAuthState({
              isAuthenticated: false,
              user: null,
              loading: false,
              error: null
            });
          }
          resolve();
        },
        error: (error) => {
          if (attempt < maxAttempts && error.status !== 401) {
            setTimeout(() => {
              this.retryAuthCheck(attempt + 1, maxAttempts).then(() => resolve());
            }, attempt * 1000); // Exponential backoff: 1s, 2s, 3s
          } else {
            this.updateAuthState({
              isAuthenticated: false,
              user: null,
              loading: false,
              error: null
            });
            resolve();
          }
        }
      });
    });
  }
  /**
   * Check authentication status with server
   */
  private checkAuthStatus(): Observable<LoginResponse> {
    // Use profile endpoint to check if user is authenticated
    // This endpoint returns user data if authenticated via cookie
    return this.http.get<User>(`${this.API_BASE}/profile`, { 
      withCredentials: true 
    }).pipe(
      // Convert user response to auth response format
      map((user: User) => ({
        access_token: 'from-cookie', // Placeholder since token is in HTTP-only cookie
        user: user,
        expires_in: 1800, // Default 30 minutes
        token_type: 'Bearer' as const
      }))
    );
  }
  /**
   * Login user with email and password
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    this.updateAuthState({ ...this.authState.value, loading: true, error: null });
    return this.http.post<LoginResponse>(`${this.API_BASE}/login`, credentials, { 
      withCredentials: true 
    })
      .pipe(
        tap((response) => {
          // Backend returns access_token on success, no success field
          if (response.access_token && response.user) {
            // Store token expiry time for refresh scheduling
            const expiresInMs = (response.expires_in || 1800) * 1000;
            this.tokenExpiresAt = Date.now() + expiresInMs;

            this.updateAuthState({
              isAuthenticated: true,
              user: response.user,
              loading: false,
              error: null
            });
          } else {
            this.updateAuthState({
              ...this.authState.value,
              loading: false,
              error: 'Login failed - invalid response format'
            });
          }
        }),
        catchError((error) => {
          return this.handleAuthError(error);
        })
      );
  }
  /**
   * Logout user and clear session
   */
  logout(): Observable<LogoutResponse> {
    this.updateAuthState({ ...this.authState.value, loading: true });
    return this.http.post<LogoutResponse>(`${this.API_BASE}/logout`, {}, {
      withCredentials: true
    })
      .pipe(
        tap(() => {
          this.clearAuthState();
          // Don't clear stored route on intentional logout - user might want to come back
          this.router.navigate(['/login']);
        }),
        catchError(error => {
          // Even if logout fails on server, clear local state
          this.clearAuthState();
          this.router.navigate(['/login']);
          return this.handleAuthError(error);
        })
      );
  }

  forceLogout(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.snackBar.open(
        'Your session has expired. Please login again.',
        'Dismiss',
        {
          duration: 4000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['session-expired-snackbar']
        }
      );
    }
    
    this.clearAuthState();
    this.router.navigate(['/login']);
  }
  /**
   * Send forgot password email
   */
  forgotPassword(request: ForgotPasswordRequest): Observable<ForgotPasswordResponse> {
    return this.http.post<ForgotPasswordResponse>(`${this.API_BASE}/forgot-password`, request)
      .pipe(
        catchError(error => this.handleAuthError(error))
      );
  }
  /**
   * Reset password with token
   */
  resetPassword(request: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(`${this.API_BASE}/reset-password`, request)
      .pipe(
        catchError(error => this.handleAuthError(error))
      );
  }
  /**
   * Refresh authentication token
   */
  refreshToken(): Observable<RefreshResponse> {
    return this.http.get<RefreshResponse>(`${this.API_BASE}/refresh`, { 
      withCredentials: true 
    })
      .pipe(
        tap(response => {
          // Backend returns access_token on success, no success field
          if (response.access_token && response.user) {
            // Store token expiry time for refresh scheduling
            const expiresInMs = (response.expires_in || 1800) * 1000;
            this.tokenExpiresAt = Date.now() + expiresInMs;

            this.updateAuthState({
              isAuthenticated: true,
              user: response.user,
              loading: false,
              error: null
            });
          }
        }),
        catchError(error => {
          // Only force logout on 401 (unauthorized) or 403 (forbidden)
          // Network errors and server errors should not immediately log out user
          if (error.status === 401 || error.status === 403) {
            this.forceLogout();
          }
          return this.handleAuthError(error);
        })
      );
  }
  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.authState.value.isAuthenticated;
  }
  /**
   * Clear current authentication error
   */
  clearError(): void {
    if (this.authState.value.error) {
      this.updateAuthState({
        ...this.authState.value,
        error: null
      });
    }
  }
  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.authState.value.user;
  }
  /**
   * Get current user role
   */
  getUserRole(): string | null {
    return this.authState.value.user?.role || null;
  }
  /**
   * Get effective auctioneer ID for data filtering
   * For super_admin: returns viewAuctioneerId if set, otherwise null (see all data)
   * For auctioneer users: returns their own auctioneerId
   * For other roles: returns their auctioneerId (if any)
   * 
   * This is the PRIMARY method components should use for filtering data by auctioneer
   */
  getAuctioneerId(): string | null {
    const user = this.authState.value.user;
    
    // For super_admin, return the viewAuctioneerId if they're viewing as an auctioneer
    if (user?.role === 'super_admin') {
      return this.authState.value.viewAuctioneerId || null;
    }
    
    // For other roles, return their own auctioneerId
    return user?.auctioneerId || null;
  }

  /**
   * Get the user's own auctioneerId (ignoring viewAuctioneerId)
   * Use this when you need the actual user's auctioneerId, not the impersonated one
   */
  getUserOwnAuctioneerId(): string | null {
    return this.authState.value.user?.auctioneerId || null;
  }

  /**
   * Get the auctioneer ID that super_admin is currently viewing
   * Alias for getAuctioneerId() for semantic clarity
   * 
   * @deprecated Use getAuctioneerId() instead - it now returns the effective ID
   */
  getViewAuctioneerId(): string | null {
    return this.getAuctioneerId();
  }

  /**
   * Check if super_admin is currently viewing as an auctioneer
   */
  isViewingAsAuctioneer(): boolean {
    const user = this.authState.value.user;
    return user?.role === 'super_admin' && !!this.authState.value.viewAuctioneerId;
  }

  /**
   * Set the auctioneer ID for super_admin to view
   * Only works for super_admin role
   */
  setViewAuctioneerId(auctioneerId: string | null): void {
    const user = this.getCurrentUser();
    if (user?.role === 'super_admin') {
      this.updateAuthState({
        ...this.authState.value,
        viewAuctioneerId: auctioneerId
      });
      
      // Persist to localStorage
      if (isPlatformBrowser(this.platformId)) {
        if (auctioneerId) {
          localStorage.setItem(this.VIEW_AUCTIONEER_KEY, auctioneerId);
        } else {
          localStorage.removeItem(this.VIEW_AUCTIONEER_KEY);
        }
      }
    }
  }
  /**
   * Check if user has specific permission (role-based)
   */
  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser();
    // Simple role-based permissions - can be expanded later
    if (!user) return false;
    // Super admin has all permissions
    if (user.role === 'super_admin') return true;
    // Add specific role-permission mappings as needed
    return false;
  }
  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const userRole = this.getUserRole();
    return userRole === role;
  }
  /**
   * Handle authentication errors
   */
  private handleAuthError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred during authentication';
    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 401) {
      errorMessage = 'Invalid email or password';
    } else if (error.status === 403) {
      errorMessage = 'Account is disabled or suspended';
    } else if (error.status === 429) {
      errorMessage = 'Too many login attempts. Please try again later';
    } else if (error.status === 0) {
      errorMessage = 'Unable to connect to server';
    }
    this.updateAuthState({ 
      ...this.authState.value, 
      loading: false, 
      error: errorMessage 
    });
    return throwError(() => new Error(errorMessage));
  }
  /**
   * Clear authentication state
   */
  private clearAuthState(): void {
    // Clear viewAuctioneerId from localStorage on logout
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.VIEW_AUCTIONEER_KEY);
    }
    
    this.updateAuthState({
      isAuthenticated: false,
      user: null,
      loading: false,
      error: null,
      viewAuctioneerId: null
    });
  }
  /**
   * Update authentication state
   */
  private updateAuthState(newState: AuthState): void {
    this.authState.next(newState);
    // Start auto-refresh timer when user becomes authenticated
    if (newState.isAuthenticated) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }
  /**
   * Start automatic token refresh timer
   * Schedules refresh before token expiry based on REFRESH_THRESHOLD_MS
   */
  private startAutoRefresh(): void {
    // Clear any existing timer
    this.stopAutoRefresh();

    const now = Date.now();
    const timeUntilExpiry = this.tokenExpiresAt - now;

    // If token already expired or no expiry set, try refresh immediately
    if (this.tokenExpiresAt > 0 && timeUntilExpiry <= 0) {
      this.refreshTokenWithRetry();
      // Note: If refresh succeeds, updateAuthState will call startAutoRefresh again
      return;
    }

    // Calculate when to refresh
    let refreshIn: number;
    if (this.tokenExpiresAt > 0) {
      // Schedule refresh before expiry
      refreshIn = Math.max(timeUntilExpiry - this.REFRESH_THRESHOLD_MS, 0);
    } else {
      // Fallback: refresh in 15 minutes if no expiry info available
      refreshIn = 15 * 60 * 1000;
    }

    this.refreshTimer = setTimeout(() => {
      this.refreshTokenWithRetry();
      // Note: If refresh succeeds, updateAuthState will call startAutoRefresh again
      // with the new tokenExpiresAt value, so no need to manually reschedule here
    }, refreshIn);
  }
  /**
   * Refresh token with retry logic
   */
  private refreshTokenWithRetry(attempt = 1, maxAttempts = 3): Promise<boolean> {
    // Prevent concurrent refresh calls (only check on first attempt)
    if (attempt === 1 && this.isRefreshing) {
      return Promise.resolve(false);
    }

    if (attempt === 1) {
      this.isRefreshing = true;
    }

    return new Promise((resolve) => {
      this.refreshToken().subscribe({
        next: () => {
          this.isRefreshing = false;
          resolve(true);
        },
        error: (error) => {
          console.log(`[Auth] Token refresh failed on attempt ${attempt}:`, error.status || 'Network error');

          // Only retry on network errors (status 0) or 5xx server errors
          // Don't retry on 401/403 as these indicate auth failure
          const shouldRetry = attempt < maxAttempts &&
                             (error.status === 0 || (error.status >= 500 && error.status < 600));

          if (shouldRetry) {
            const retryDelay = attempt * 2000; // Exponential backoff: 2s, 4s, 6s
            console.log(`[Auth] Will retry in ${retryDelay/1000}s...`);

            setTimeout(() => {
              this.refreshTokenWithRetry(attempt + 1, maxAttempts).then(resolve);
            }, retryDelay);
          } else {
            this.isRefreshing = false;
            if (error.status === 401 || error.status === 403) {
              console.log('[Auth] Authentication failed, user will be logged out');
            } else {
              console.log('[Auth] Max retries reached, giving up');
            }
            // If refresh fails due to auth errors, user will be logged out automatically by the refresh method
            resolve(false);
          }
        }
      });
    });
  }
  /**
   * Stop automatic token refresh timer
   */
  private stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Set up visibility change listener to refresh token when tab becomes visible
   */
  private setupVisibilityListener(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.visibilityChangeHandler = () => {
      // Only check when tab becomes visible and user is authenticated
      if (!document.hidden && this.isAuthenticated()) {
        const now = Date.now();
        const timeUntilExpiry = this.tokenExpiresAt - now;

        // If token expires within threshold, refresh immediately
        // Note: refreshToken() -> updateAuthState() -> startAutoRefresh() handles rescheduling
        if (timeUntilExpiry <= this.REFRESH_THRESHOLD_MS) {
          this.refreshTokenWithRetry();
        }
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Get current user value synchronously
   */
  get currentUserValue(): User | null {
    return this.authState.value.user;
  }

  /**
   * Cleanup when service is destroyed
   */
  ngOnDestroy(): void {
    this.stopAutoRefresh();

    // Remove visibility change listener
    if (this.visibilityChangeHandler && isPlatformBrowser(this.platformId)) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
  }
}
